import React from 'react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { resolveChartTheme } from './chartTheme';
import './chart.css';

/* =============================================================================
 * Design-system charts — token-driven wrappers around Recharts so every chart
 * in the app shares the same axes, grid, gradient and tooltip language.
 *
 *   <TrendAreaChart  data={data} xKey="date" dataKey="value" />   // trends
 *   <TrendLineChart  data={data} series={[{dataKey, name, color}]} /> // multi-series
 *   <CategoryBarChart data={data} xKey="label" dataKey="value" />  // comparisons
 *   <Sparkline       data={data} dataKey="value" />               // inline KPI trend
 *   <DonutChart      data={[{name, value}]} centerValue="42" />   // breakdowns
 *
 * Common props: data, height, formatValue (tooltip), formatYAxis (axis ticks),
 * labelFormatter (tooltip header), color/colors (override the token palette).
 * Colors default to the --color-chart-* tokens via resolveChartTheme().
 * ========================================================================== */

const DEFAULT_MARGIN = { top: 16, right: 12, left: 0, bottom: 0 };

/* Normalize a single dataKey OR a `series` array into one consistent shape. */
const toSeries = (series, dataKey, name, color, palette) => {
  if (Array.isArray(series) && series.length) {
    return series.map((s, i) => ({
      key: s.dataKey || s.key,
      name: s.name || s.dataKey || s.key,
      color: s.color || palette[i % palette.length],
    }));
  }
  return [{ key: dataKey, name: name || dataKey, color: color || palette[0] }];
};

/* Shared axis config so every cartesian chart reads identically. */
const axisProps = (theme) => ({
  tickLine: false,
  axisLine: false,
  tick: { fontSize: 12, fill: theme.axis },
});

/** DS-styled tooltip used as the `content` for Recharts <Tooltip>. */
export const ChartTooltip = ({
  active, payload, label, formatValue, labelFormatter,
}) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="ps-chart-tooltip" role="status">
      <div className="ps-chart-tooltip__label">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      {payload.map((p, i) => (
        <div className="ps-chart-tooltip__row" key={p.dataKey ?? i}>
          <span className="ps-chart-tooltip__dot" style={{ background: p.color || p.stroke }} />
          {p.name ? <span className="ps-chart-tooltip__name">{p.name}</span> : null}
          <span className="ps-chart-tooltip__value">
            {formatValue ? formatValue(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

let gradSeq = 0;

export const TrendAreaChart = ({
  data = [],
  xKey = 'date',
  dataKey = 'value',
  name,
  height = 320,
  color,
  formatValue,
  formatYAxis,
  labelFormatter,
  margin,
}) => {
  const theme = resolveChartTheme();
  const stroke = color || theme.colors[0];
  // stable, unique gradient id per instance (avoids defs collisions)
  const gradId = React.useMemo(() => `psArea${(gradSeq += 1)}`, []);

  return (
    <div className="ps-chart">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={margin || DEFAULT_MARGIN}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.26} />
              <stop offset="95%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke={theme.grid} vertical={false} />
          <XAxis dataKey={xKey} tickMargin={10} {...axisProps(theme)} />
          <YAxis tickFormatter={formatYAxis} width={48} {...axisProps(theme)} />
          <Tooltip
            cursor={{ stroke: theme.grid, strokeWidth: 1 }}
            content={<ChartTooltip formatValue={formatValue} labelFormatter={labelFormatter} />}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            name={name}
            stroke={stroke}
            strokeWidth={2.5}
            fill={`url(#${gradId})`}
            fillOpacity={1}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-card)', fill: stroke }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Line chart — single series (dataKey) or multi-series (series=[{dataKey,name,color}]). */
export const TrendLineChart = ({
  data = [],
  xKey = 'date',
  dataKey = 'value',
  series,
  name,
  height = 320,
  color,
  formatValue,
  formatYAxis,
  labelFormatter,
  margin,
  showGrid = true,
}) => {
  const theme = resolveChartTheme();
  const lines = toSeries(series, dataKey, name, color, theme.colors);

  return (
    <div className="ps-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={margin || DEFAULT_MARGIN}>
          {showGrid && <CartesianGrid strokeDasharray="4 4" stroke={theme.grid} vertical={false} />}
          <XAxis dataKey={xKey} tickMargin={10} {...axisProps(theme)} />
          <YAxis tickFormatter={formatYAxis} width={48} {...axisProps(theme)} />
          <Tooltip
            cursor={{ stroke: theme.grid, strokeWidth: 1 }}
            content={<ChartTooltip formatValue={formatValue} labelFormatter={labelFormatter} />}
          />
          {lines.map((l) => (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              name={l.name}
              stroke={l.color}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-card)', fill: l.color }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Bar chart for category comparisons — single or multi (grouped) series. */
export const CategoryBarChart = ({
  data = [],
  xKey = 'label',
  dataKey = 'value',
  series,
  name,
  height = 320,
  color,
  formatValue,
  formatYAxis,
  labelFormatter,
  margin,
  barSize = 28,
}) => {
  const theme = resolveChartTheme();
  const bars = toSeries(series, dataKey, name, color, theme.colors);

  return (
    <div className="ps-chart">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={margin || DEFAULT_MARGIN} barCategoryGap="28%">
          <CartesianGrid strokeDasharray="4 4" stroke={theme.grid} vertical={false} />
          <XAxis dataKey={xKey} tickMargin={10} {...axisProps(theme)} />
          <YAxis tickFormatter={formatYAxis} width={48} {...axisProps(theme)} />
          <Tooltip
            cursor={{ fill: theme.grid, fillOpacity: 0.18 }}
            content={<ChartTooltip formatValue={formatValue} labelFormatter={labelFormatter} />}
          />
          {bars.map((b) => (
            <Bar
              key={b.key}
              dataKey={b.key}
              name={b.name}
              fill={b.color}
              maxBarSize={barSize}
              radius={[6, 6, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Compact, axis-less trend for KPI cards / table cells. */
export const Sparkline = ({
  data = [],
  dataKey = 'value',
  height = 40,
  color,
  area = true,
  strokeWidth = 2,
}) => {
  const theme = resolveChartTheme();
  const stroke = color || theme.colors[0];
  const gradId = React.useMemo(() => `psSpark${(gradSeq += 1)}`, []);

  return (
    <div className="ps-chart ps-chart--spark">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          {area && (
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.22} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={stroke}
            strokeWidth={strokeWidth}
            fill={area ? `url(#${gradId})` : 'none'}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Donut / breakdown chart with optional center label and legend. */
export const DonutChart = ({
  data = [],
  dataKey = 'value',
  nameKey = 'name',
  height = 220,
  colors,
  innerRadius = 58,
  outerRadius = 88,
  formatValue,
  centerLabel,
  centerValue,
  showLegend = true,
}) => {
  const theme = resolveChartTheme();
  const palette = colors || theme.colors;

  return (
    <div className="ps-chart ps-chart--donut">
      <div className="ps-chart__donut-canvas" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<ChartTooltip formatValue={formatValue} />} />
            <Pie
              data={data}
              dataKey={dataKey}
              nameKey={nameKey}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              stroke="var(--color-card)"
              strokeWidth={2}
            >
              {data.map((entry, i) => (
                <Cell key={entry[nameKey] ?? i} fill={palette[i % palette.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {(centerLabel || centerValue != null) && (
          <div className="ps-chart__donut-center">
            {centerValue != null && <span className="ps-chart__donut-value">{centerValue}</span>}
            {centerLabel && <span className="ps-chart__donut-label">{centerLabel}</span>}
          </div>
        )}
      </div>
      {showLegend && (
        <ul className="ps-chart__legend">
          {data.map((entry, i) => (
            <li className="ps-chart__legend-item" key={entry[nameKey] ?? i}>
              <span className="ps-chart__legend-dot" style={{ background: palette[i % palette.length] }} />
              <span className="ps-chart__legend-name">{entry[nameKey]}</span>
              <span className="ps-chart__legend-value">
                {formatValue ? formatValue(entry[dataKey]) : entry[dataKey]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TrendAreaChart;
